import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import LoadingComponent from '../loadingComponent';
import Authorizer from '../session/authorizer';
import { PERMISSIONS } from '../../session/authorize';
import ClassetStudentSearchFilter from '../Search/ClassetStudentSearchFilter';
import withRouter from '../lib/withRouter';
import Service from '../../services';
import { baseUrlAdmin } from '../../store/apiConstants';
import './RiskAdjustmentHistory.scss';

class RiskAdjustmentHistory extends Component {
    constructor(props) {
        super(props);
        this.service = new Service();
        this.toast = React.createRef();

        this.state = {
            loading: false,
            history: [],
            filters: {
                studentId: '',
                eventType: null,
                startDate: null,
                endDate: null,
                limit: 100
            }
        };

        this.eventTypeOptions = [
            { label: 'All Events', value: null },
            { label: 'Risk Adjusted', value: 'RISK_ADJUSTED' },
            { label: 'Risk Band Changed', value: 'RISK_BAND_CHANGED' },
            { label: 'Promise Kept', value: 'PROMISE_KEPT' },
            { label: 'Promise Breached', value: 'PROMISE_BREACHED' },
            { label: 'Payment Received', value: 'PAYMENT_RECEIVED' },
            { label: 'Call Outcome Logged', value: 'CALL_OUTCOME_LOGGED' },
            { label: 'Engagement Penalty Updated', value: 'ENGAGEMENT_PENALTY_UPDATED' },
            { label: 'Action Completed', value: 'ACTION_COMPLETED' },
            { label: 'System Calibration', value: 'SYSTEM_CALIBRATION' }
        ];

        this.limitOptions = [
            { label: 'Last 50 events', value: 50 },
            { label: 'Last 100 events', value: 100 },
            { label: 'Last 200 events', value: 200 },
            { label: 'Last 500 events', value: 500 }
        ];
    }

    componentDidMount() {
        const studentId = this.props.studentId || this.props.params?.studentId;
        if (studentId) {
            this.setState(prevState => ({
                filters: { ...prevState.filters, studentId }
            }), this.fetchHistory);
        }
    }

    showToast = (severity, summary, detail) => {
        this.toast.current?.show({
            severity: severity,
            summary: summary,
            detail: detail,
            life: 3000
        });
    }

    fetchHistory = async () => {
        const academicYear = localStorage.getItem('userAcademicYear');
        const orgId = localStorage.getItem('orgId');
        const { filters } = this.state;





        if (!filters.studentId) {
            console.warn('⚠️ No studentId in filters');
            this.showToast('warn', 'Student Required', 'Please select a student to view learning history');
            this.setState({ history: [] });
            return;
        }

        this.setState({ loading: true });

        try {
            let url = `${baseUrlAdmin}/fee-decision/learning/student/${filters.studentId}?orgId=${orgId}&academicYear=${academicYear}`;

            if (filters.eventType) url += `&eventType=${filters.eventType}`;
            if (filters.limit) url += `&limit=${filters.limit}`;



            
            const response = await this.service.get(url, true);

            
            if (response?.status && response?.res?.success) {
                const data = response.res.data || [];

                const sorted = data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));


                this.setState({ history: sorted });
                
                if (sorted.length === 0) {
                    this.showToast('info', 'No History', 'No learning events found for this student');
                } else {
                    this.showToast('success', 'Success', `Loaded ${sorted.length} learning events`);
                }
            } else {
                console.warn('⚠️ API returned unsuccessful response:', response);
                this.setState({ history: [] });
                this.showToast('warn', 'No Data', 'No learning history available for this student');
            }
        } catch (error) {
            console.error('❌ Error fetching learning history:', error);
            console.error('❌ Error details:', {
                message: error.message,
                stack: error.stack
            });
            this.showToast('error', 'Error', 'Failed to fetch learning history');
            this.setState({ history: [] });
        } finally {
            this.setState({ loading: false });

        }
    }

    handleFilterChange = (key, value) => {
        this.setState(prevState => ({
            filters: { ...prevState.filters, [key]: value }
        }));
    }

    handleStudentSelect = (payload) => {

        
        // Extract studentId from the payload (ClassetStudentSearchFilter returns array in studentIds)
        const studentId = payload.studentIds && payload.studentIds.length > 0 ? payload.studentIds[0] : null;


        
        this.setState(prevState => ({
            filters: { ...prevState.filters, studentId: studentId }
        }), () => {

            // Automatically fetch history when student is selected
            if (studentId) {

                this.fetchHistory();
            } else {

                this.setState({ history: [] });
            }
        });
    }

    applyFilters = () => {
        this.fetchHistory();
    }

    clearFilters = () => {
        this.setState({
            filters: {
                studentId: '',
                eventType: null,
                startDate: null,
                endDate: null,
                limit: 100
            },
            history: []
        });
    }

    getFilteredHistory = () => {
        const { history, filters } = this.state;
        let filtered = [...history];

        if (filters.startDate || filters.endDate) {
            const start = filters.startDate ? new Date(filters.startDate) : null;
            const end = filters.endDate ? new Date(filters.endDate) : null;

            if (start) start.setHours(0, 0, 0, 0);
            if (end) end.setHours(23, 59, 59, 999);

            filtered = filtered.filter(item => {
                const createdAt = new Date(item.createdAt);
                if (start && createdAt < start) return false;
                if (end && createdAt > end) return false;
                return true;
            });
        }

        return filtered;
    }

    // Templates
    dateTimeBodyTemplate = (rowData) => {
        if (!rowData.createdAt) return '-';
        // Use local time for learning logs as created events are timestamps
        const dateObj = new Date(rowData.createdAt);
        return (
            <div className="datetime-cell">
                <div className="date">{dateObj.toLocaleDateString('en-GB')}</div>
                <div className="time">{dateObj.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</div>
            </div>
        );
    }

    eventTypeBodyTemplate = (rowData) => {
        const severityMap = {
            'RISK_ADJUSTED': 'info',
            'RISK_BAND_CHANGED': 'warning',
            'PROMISE_KEPT': 'success',
            'PROMISE_BREACHED': 'danger',
            'PAYMENT_RECEIVED': 'success',
            'CALL_OUTCOME_LOGGED': 'info',
            'ENGAGEMENT_PENALTY_UPDATED': 'danger',
            'ACTION_COMPLETED': 'primary',
            'SYSTEM_CALIBRATION': 'secondary'
        };

        return (
            <Tag 
                value={rowData.eventType.replace(/_/g, ' ')} 
                severity={severityMap[rowData.eventType] || 'info'} 
                rounded
            />
        );
    }

    getRiskTag = (band) => {
        if (!band) return '-';
        let severity = null;
        let style = null;
        
        switch (band.toUpperCase()) {
            case 'CRITICAL': style = { backgroundColor: '#FFA6BC', color: '#BF0031' }; break;
            case 'HIGH': style = { backgroundColor: '#FFEDD5', color: '#C2410C' }; break; // Explicit Dark Orange
            case 'MEDIUM': style = { backgroundColor: '#FEF9C3', color: '#854D0E' }; break; // Explicit Yellow
            case 'LOW': style = { backgroundColor: '#DCFCE7', color: '#268900' }; break;
            default: style = { backgroundColor: '#E6E8EA', color: '#4B5563' };
        }

        return <Tag value={band} severity={severity} style={style} />;
    }

    valueChangeBodyTemplate = (rowData) => {
        if (rowData.previousRiskScore !== undefined && rowData.newRiskScore !== undefined) {
            const diff = rowData.newRiskScore - rowData.previousRiskScore;
            const color = diff > 0 ? 'text-red-500' : 'text-green-500';
            return (
                <div className="value-change">
                    <span className="text-500">{rowData.previousRiskScore}</span>
                    <i className="pi pi-arrow-right mx-2" style={{margin: '0 8px', fontSize: '0.8em'}}></i>
                    <span className="font-bold">{rowData.newRiskScore}</span>
                    <span className={`ml-2 text-xs ${color}`}>({diff > 0 ? '+' : ''}{diff})</span>
                </div>
            );
        }

        if (rowData.previousRiskBand || rowData.newRiskBand) {
            return (
                <div className="value-change flex align-items-center">
                    <span className="text-500">{this.getRiskTag(rowData.previousRiskBand)}</span>
                    <i className="pi pi-arrow-right mx-2" style={{margin: '0 8px', fontSize: '0.8em'}}></i>
                    <span className="font-bold">{this.getRiskTag(rowData.newRiskBand)}</span>
                </div>
            );
        }

        return <span className="text-gray-500">-</span>;
    }

    justificationBodyTemplate = (rowData) => {
        return (
            <div className="justification-cell">
                {rowData.justification || rowData.overrideJustification || rowData.message || '-'}
                {rowData.ruleApplied && (
                    <div className="text-xs text-500 mt-1">
                        <i className="pi pi-cog mr-1"></i>
                        Rule: {rowData.ruleApplied.ruleName}
                    </div>
                )}
                {rowData.performedByName && rowData.performedByName !== 'system' && (
                    <div className="text-xs text-500 mt-1">
                        <i className="pi pi-user mr-1"></i>
                        By: {rowData.performedByName}
                    </div>
                )}
            </div>
        );
    }

    exportToCSV = () => {
        const filteredHistory = this.getFilteredHistory();
        if (filteredHistory.length === 0) {
            this.showToast('warn', 'No Data', 'No data available to export');
            return;
        }

        const csvData = filteredHistory.map(item => ({
            'Date': new Date(item.createdAt).toLocaleDateString('en-GB'),
            'Time': new Date(item.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}),
            'Event Type': item.eventType.replace(/_/g, ' '),
            'Previous Risk Score': item.previousRiskScore || '-',
            'New Risk Score': item.newRiskScore || '-',
            'Score Change': item.riskScoreChange || '-',
            'Previous Risk Band': item.previousRiskBand || '-',
            'New Risk Band': item.newRiskBand || '-',
            'Details': item.justification || item.message || '-',
            'Performed By': item.performedByName || 'System',
            'Automated': item.automated ? 'Yes' : 'No'
        }));

        const csv = [
            Object.keys(csvData[0]).join(','),
            ...csvData.map(row => Object.values(row).map(val => 
                typeof val === 'string' && val.includes(',') ? `"${val}"` : val
            ).join(','))
        ].join('\n');

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `risk_adjustment_history_${new Date().getTime()}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        this.showToast('success', 'Export Successful', 'History exported successfully');
    }

    render() {
        const { loading, filters } = this.state;
        const filteredHistory = this.getFilteredHistory();

        if (loading && filteredHistory.length === 0) {
            return <LoadingComponent />;
        }

        const summary = filteredHistory.reduce((acc, item) => {
            acc.total += 1;
            if (item.eventType === 'PROMISE_KEPT') acc.promiseKept += 1;
            if (item.eventType === 'PROMISE_BREACHED') acc.promiseBreached += 1;
            if (item.eventType === 'PAYMENT_RECEIVED') acc.paymentReceived += 1;
            if (item.eventType === 'RISK_ADJUSTED') acc.riskAdjusted += 1;
            return acc;
        }, { total: 0, promiseKept: 0, promiseBreached: 0, paymentReceived: 0, riskAdjusted: 0 });

        return (
            <div className="risk-adjustment-history">
                <Toast ref={this.toast} position="bottom-right" />

                <div className="p-grid">
                    <div className="p-col-12">
                        <Card className="header-card mb-3">
                            <div className="flex align-items-center justify-content-between">
                                <div>
                                    <h2 className="m-0 text-900">Risk Adjustment History</h2>
                                    <p className="text-500 mb-0 mt-1">Rules-based learning loop that explains how outcomes change risk over time. (Module 9: Learning Loop)</p>
                                </div>
                                <div className="text-right flex gap-2">
                                    <Authorizer permId={PERMISSIONS.FDE_VIEW_LEARNING_HISTORY}>
                                        <Button
                                            label="Export"
                                            icon="pi pi-download"
                                            className="p-button-outlined p-button-secondary"
                                            onClick={this.exportToCSV}
                                            tooltip="Export to CSV"
                                            disabled={this.getFilteredHistory().length === 0}
                                        />
                                    </Authorizer>
                                    <Button
                                        icon="pi pi-refresh"
                                        className="p-button-outlined p-button-secondary"
                                        onClick={this.fetchHistory}
                                        tooltip="Refresh"
                                    />
                                </div>
                            </div>
                        </Card>

                        <Card className="learning-note mb-3">
                            <div className="note-content">
                                <i className="pi pi-info-circle"></i>
                                <span>This is a rules-based learning loop (Module 9). All risk adjustments are logged for transparency and auditability (Module 11: Governance).</span>
                                <div className="button-group-inline">
                                    <Button
                                        label="View Promises & Breaches"
                                        className="p-button-text p-button-sm"
                                        onClick={() => this.props.navigate('/financeNew/PromisesBreaches')}
                                    />
                                    <Button
                                        label="View Audit Trail"
                                        className="p-button-text p-button-sm"
                                        onClick={() => this.props.navigate('/financeNew/AuditTrail')}
                                    />
                                </div>
                            </div>
                        </Card>

                        <Card className="filter-card mb-3">
                            <div className="filter-section-wrapper">
                                {/* Student Search - Full width */}
                                <div className="student-search-container">
                                    <ClassetStudentSearchFilter
                                        singleSelectInput={true}
                                        setValues={this.handleStudentSelect}
                                    />
                                </div>

                                {/* Other Filters in a Row */}
                                <div className="filters-row">
                                    <div className="filter-group">
                                        <label className="filter-label">Event Type</label>
                                        <Dropdown
                                            value={filters.eventType}
                                            options={this.eventTypeOptions}
                                            onChange={(e) => this.handleFilterChange('eventType', e.value)}
                                            placeholder="All Events"
                                            showClear
                                            className="filter-dropdown"
                                            style={{ width: '100%' }}
                                        />
                                    </div>

                                    <div className="filter-group">
                                        <label className="filter-label">Start Date</label>
                                        <Calendar
                                            value={filters.startDate}
                                            onChange={(e) => this.handleFilterChange('startDate', e.value)}
                                            placeholder="Start Date"
                                            showIcon
                                            dateFormat="dd/mm/yy"
                                            className="filter-calendar"
                                            style={{ width: '100%' }}
                                        />
                                    </div>

                                    <div className="filter-group">
                                        <label className="filter-label">End Date</label>
                                        <Calendar
                                            value={filters.endDate}
                                            onChange={(e) => this.handleFilterChange('endDate', e.value)}
                                            placeholder="End Date"
                                            showIcon
                                            dateFormat="dd/mm/yy"
                                            className="filter-calendar"
                                            style={{ width: '100%' }}
                                        />
                                    </div>

                                    <div className="filter-group">
                                        <label className="filter-label">History Limit</label>
                                        <Dropdown
                                            value={filters.limit}
                                            options={this.limitOptions}
                                            onChange={(e) => this.handleFilterChange('limit', e.value)}
                                            className="filter-dropdown"
                                            style={{ width: '100%' }}
                                        />
                                    </div>

                                    <div className="filter-group filter-actions">
                                        <label className="filter-label">&nbsp;</label>
                                        <div className="action-buttons">
                                            <Button 
                                                label="Apply Filters" 
                                                icon="pi pi-search" 
                                                onClick={this.applyFilters}
                                                className="p-button-primary"
                                            />
                                            <Button
                                                icon="pi pi-filter-slash"
                                                className="p-button-secondary p-button-outlined"
                                                onClick={this.clearFilters}
                                                tooltip="Clear All Filters"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Card>

                        <div className="summary-grid">
                            <div className="summary-card">
                                <div className="summary-label">Total Events</div>
                                <div className="summary-value">{summary.total}</div>
                            </div>
                            <div className="summary-card success">
                                <div className="summary-label">Promise Kept</div>
                                <div className="summary-value">{summary.promiseKept}</div>
                            </div>
                            <div className="summary-card danger">
                                <div className="summary-label">Promise Breached</div>
                                <div className="summary-value">{summary.promiseBreached}</div>
                            </div>
                            <div className="summary-card info">
                                <div className="summary-label">Payments Received</div>
                                <div className="summary-value">{summary.paymentReceived}</div>
                            </div>
                            <div className="summary-card warning">
                                <div className="summary-label">Risk Adjustments</div>
                                <div className="summary-value">{summary.riskAdjusted}</div>
                            </div>
                        </div>

                        <div className="card">
                            <DataTable
                                value={filteredHistory}
                                paginator
                                rows={20}
                                loading={loading}
                                emptyMessage="Select a student to view the learning log. The system logs all risk adjustments automatically."
                                stripedRows
                                rowHover
                                className="p-datatable-sm"
                                rowsPerPageOptions={[10, 20, 50, 100]}
                                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} events"
                                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                            >
                                <Column
                                    field="createdAt"
                                    header="Date / Time"
                                    body={this.dateTimeBodyTemplate}
                                    style={{ width: '12%' }}
                                    sortable
                                />
                                <Column
                                    field="eventType"
                                    header="Event"
                                    body={this.eventTypeBodyTemplate}
                                    style={{ width: '14%' }}
                                    sortable
                                />
                                <Column
                                    header="Risk Change"
                                    body={this.valueChangeBodyTemplate}
                                    style={{ width: '16%' }}
                                />
                                <Column
                                    header="Details / Justification"
                                    body={this.justificationBodyTemplate}
                                />
                            </DataTable>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    user: state.user
});

export default connect(mapStateToProps)(withRouter(RiskAdjustmentHistory));

